/**
 * SosSettings — operator controls for SOS Phase 2 advisory escalation.
 * Enable/disable escalation, set per-level "no driver reply" thresholds, and
 * toggle automatic emergency-contact notification on escalation. Reporting/
 * config only — never affects dispatch.
 */
import React, { useState, useEffect } from "react";
import { Timer, Save, Loader2, ShieldCheck, BellRing } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

export const SosSettings = () => {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/sos/config").then(({ data }) => setCfg(data))
      .catch((e) => toast.error(formatApiError(e) || "Failed to load escalation settings"));
  }, []);

  const upd = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/sos/config", {
        escalation_enabled: cfg.escalation_enabled,
        emergency_threshold_secs: Number(cfg.emergency_threshold_secs) || 60,
        assistance_threshold_secs: Number(cfg.assistance_threshold_secs) || 180,
        auto_notify_contacts: cfg.auto_notify_contacts,
        nearby_limit: Number(cfg.nearby_limit) || 5,
      });
      setCfg(data); toast.success("Escalation settings saved.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (!cfg) return (
    <div className="p-8 text-sm text-muted-foreground flex items-center gap-2" data-testid="sos-settings-loading">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading escalation settings…
    </div>
  );

  return (
    <div className="space-y-4 max-w-2xl" data-testid="sos-settings">
      <div className="border border-border rounded-xl p-4 bg-white space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-bold flex items-center gap-1.5"><Timer className="w-4 h-4" /> Escalation timers</h3>
            <p className="text-xs text-muted-foreground mt-0.5">If a driver sends NO reply within the threshold, the Control Room is re-alerted (advisory — never auto-dispatches).</p>
          </div>
          <button onClick={() => upd({ escalation_enabled: !cfg.escalation_enabled })} data-testid="sos-settings-enabled-toggle"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${cfg.escalation_enabled ? "bg-[#065F46]" : "bg-gray-300"}`}>
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${cfg.escalation_enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Silent Emergency threshold (seconds)</label>
            <input type="number" min={15} max={3600} value={cfg.emergency_threshold_secs} onChange={(e) => upd({ emergency_threshold_secs: e.target.value })} data-testid="sos-settings-emergency-threshold"
              className="mt-1 w-full border border-border rounded-lg px-2.5 py-1.5 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Assistance threshold (seconds)</label>
            <input type="number" min={15} max={7200} value={cfg.assistance_threshold_secs} onChange={(e) => upd({ assistance_threshold_secs: e.target.value })} data-testid="sos-settings-assistance-threshold"
              className="mt-1 w-full border border-border rounded-lg px-2.5 py-1.5 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">"Nearest drivers" list size</label>
            <input type="number" min={1} max={20} value={cfg.nearby_limit} onChange={(e) => upd({ nearby_limit: e.target.value })} data-testid="sos-settings-nearby-limit"
              className="mt-1 w-full border border-border rounded-lg px-2.5 py-1.5 text-sm" />
          </div>
        </div>

        <div className="flex items-start justify-between gap-3 border-t border-border pt-3">
          <div>
            <div className="text-xs font-semibold flex items-center gap-1.5"><BellRing className="w-3.5 h-3.5 text-[#B45309]" /> Auto-notify emergency contacts on escalation</div>
            <p className="text-[11px] text-muted-foreground mt-0.5">When an incident escalates, automatically send the driver's emergency contacts a safety alert (audited).</p>
          </div>
          <button onClick={() => upd({ auto_notify_contacts: !cfg.auto_notify_contacts })} data-testid="sos-settings-autonotify-toggle"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${cfg.auto_notify_contacts ? "bg-[#065F46]" : "bg-gray-300"}`}>
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${cfg.auto_notify_contacts ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>

        <button onClick={save} disabled={busy} data-testid="sos-settings-save"
          className="text-xs font-semibold bg-[#0A0A0A] text-white rounded-lg px-3 py-2 flex items-center gap-1.5 disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save settings
        </button>
      </div>

      <div className="text-[11px] bg-[#FFFBEB] text-[#92400E] border border-[#FCD34D] rounded-lg px-3 py-2 flex items-start gap-1.5">
        <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
        <span>Escalation is <b>advisory</b>: it re-alerts the Control Room and (optionally) notifies emergency contacts. It never assigns, dispatches or changes a ride. The dispatcher always decides.</span>
      </div>
    </div>
  );
};

export default SosSettings;
